import React from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';
import { PreviewProvider } from '../contexts/PreviewContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { CustomColorsProvider } from '../contexts/CustomColorsContext';
import { NavbarComponent } from './NavbarComponent';
import { HeroComponent } from './HeroComponent';
import { FeaturesComponent } from './FeaturesComponent';
import { CtaComponent } from './CtaComponent';
import { FooterComponent } from './FooterComponent';

// Sample preview data for components
const previewData = {
  navbar: {
    siteName: 'YourBrand',
    logo: {
      mode: 'text' as const,
      text: 'YourBrand',
      textSize: 'large' as const,
      textWeight: 'bold' as const,
    },
    links: [
      { text: 'Home', url: '/', order: 0 },
      { text: 'About', url: '/about', order: 1 },
      { text: 'Services', url: '/services', order: 2 },
      { text: 'Contact', url: '/contact', order: 3 },
    ],
    alignment: 'space-between' as const,
    sticky: false,
  },
  hero: {
    tagline: 'Welcome to the Future',
    title: 'Transform Your Business Today',
    subtitle:
      'Build amazing digital experiences with our powerful platform. Join thousands of companies already growing with us.',
    buttons: [
      { text: 'Get Started', url: '/signup', style: 'primary' as const },
      { text: 'Learn More', url: '/about', style: 'secondary' as const },
    ],
    alignment: 'center' as const,
    size: 'medium' as const,
  },
  features: {
    title: 'Everything You Need',
    features: [
      {
        icon: 'zap',
        title: 'Lightning Fast',
        description: 'Optimized performance ensures your website loads instantly.',
      },
      {
        icon: 'shield',
        title: 'Secure',
        description: 'Bank-level encryption protects your data.',
      },
      {
        icon: 'users',
        title: 'Collaboration',
        description: 'Work together seamlessly with your team.',
      },
    ],
    layout: 'grid' as const,
    columns: '3' as const,
  },
  cta: {
    title: 'Ready to Get Started?',
    subtitle: 'Join thousands of businesses already growing with us',
    buttons: [{ text: 'Start Free Trial', url: '/signup', style: 'primary' as const }],
    alignment: 'center' as const,
    style: 'gradient' as const,
  },
  footer: {
    siteName: 'YourBrand',
    description: 'Building the future of digital experiences',
    copyright: '© 2024 YourBrand. All rights reserved.',
    links: [
      { text: 'Privacy', url: '/privacy', category: 'Legal' },
      { text: 'Terms', url: '/terms', category: 'Legal' },
    ],
    socialLinks: [
      { platform: 'Twitter', url: 'https://twitter.com' },
      { platform: 'LinkedIn', url: 'https://linkedin.com' },
    ],
  },
};

// Create component props in the format expected by components
const createComponentProp = (type: string, data: any) => ({
  id: `preview-${type}`,
  type,
  data,
  order: 0,
  visible: true,
});

interface ThemePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  themeId: string;
  themeName: string;
  customColors?: Record<string, Record<string, string>>;
  onSelectTheme: (themeId: string) => void;
}

export const ThemePreviewModal: React.FC<ThemePreviewModalProps> = ({
  isOpen,
  onClose,
  themeId,
  themeName,
  customColors,
  onSelectTheme,
}) => {
  // Prevent body scroll when modal is open - must be before early return to follow hooks rules
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelectTheme = () => {
    onSelectTheme(themeId);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Container */}
      <div className="absolute inset-4 md:inset-8 lg:inset-12 flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Theme Preview: {themeName}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              See how your website will look with this theme
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Preview Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-gray-100">
          <div className="max-w-6xl mx-auto my-6">
            {/* Browser Frame */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
              {/* Fake Browser Bar */}
              <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center space-x-2">
                <div className="flex space-x-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-white rounded-md px-3 py-1 text-sm text-gray-500 border border-gray-200">
                    yourbrand.com
                  </div>
                </div>
              </div>

              {/* Preview Website */}
              <PreviewProvider isPreviewMode={true} siteId="theme-preview">
                <ThemeProvider theme={themeId}>
                  <CustomColorsProvider customColors={customColors || {}}>
                    <div className="min-h-[600px]">
                      {/* Navbar */}
                      <NavbarComponent
                        component={createComponentProp('navbar', previewData.navbar) as any}
                      />

                      {/* Hero */}
                      <HeroComponent
                        component={createComponentProp('hero', previewData.hero) as any}
                      />

                      {/* Features */}
                      <FeaturesComponent
                        component={createComponentProp('features', previewData.features) as any}
                      />

                      {/* CTA */}
                      <CtaComponent
                        component={createComponentProp('cta', previewData.cta) as any}
                      />

                      {/* Footer */}
                      <FooterComponent
                        component={createComponentProp('footer', previewData.footer) as any}
                      />
                    </div>
                  </CustomColorsProvider>
                </ThemeProvider>
              </PreviewProvider>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-500">
            Preview shows sample content with the <strong>{themeName}</strong> theme colors
          </p>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelectTheme}
              className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
            >
              <Check className="w-4 h-4" />
              <span>Select This Theme</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
